import React, { FC, useCallback, useState } from 'react'
import styles from 'styles/components/card.module.scss'
import { push } from 'connected-react-router'
import { State } from 'index'
import { useDispatch, useSelector } from 'react-redux'
import { Card, CardInner, CardTitle } from 'components/Card'
import { HorizontalLine, Spacer } from 'components/Utility'
import { Caution, SubCaution } from 'components/Typography'
import { BlueButton } from 'components/Button'
import { TextInput } from 'components/Form'
import { useQueryShopBalance } from 'hooks/revenue'
import { useNumberChangeEvent } from 'hooks/form'
import { MIN_TRANSFERABLE_AMOUNT } from 'constants/stripe'
import { getShopRef } from 'reducks/store/selectors'

const BalanceCard: FC = React.memo(() => {
  const dispatch = useDispatch()
  const shopRef = useSelector((state: State) => getShopRef(state))
  const balance = useQueryShopBalance(shopRef?.id)
  const { availableAmount, pendingAmount } = balance
  const [partialTransferAmount, setPartialTransferAmount] = useState<number>(0)
  const handlePartialTransferAmount = useNumberChangeEvent(setPartialTransferAmount)

  const isAbleToTransfer = availableAmount > MIN_TRANSFERABLE_AMOUNT
  const isAbleToPartialTransfer =
    partialTransferAmount > MIN_TRANSFERABLE_AMOUNT && availableAmount >= partialTransferAmount

  const goToTransfer = useCallback(() => dispatch(push('/account/revenue/bank_transfer')), [])
  const goToPartialTransfer = useCallback(() => {
    dispatch(push(`/account/revenue/bank_transfer?amount=${partialTransferAmount}`))
  }, [partialTransferAmount])

  return (
    <Card>
      <CardInner>
        <CardTitle title={'残高'} />
        <Spacer size={20} />
        <Card isGrey={true}>
          <CardInner>
            <p className={styles.balance__headline}>現時点入金対象額</p>
            <Spacer size={8} />
            <p>
              <span className={styles.balance__amount}>{availableAmount.toLocaleString()}</span>
              <span className={styles.balance__currency}>円</span>
            </p>
          </CardInner>
        </Card>
        <Spacer size={8} />
        <Card isGrey={true}>
          <CardInner>
            <p className={styles.balance__headline}>まもなくご利用可能</p>
            <Spacer size={8} />
            <p>
              <span className={styles.balance__amount}>{pendingAmount.toLocaleString()}</span>
              <span className={styles.balance__currency}>円</span>
            </p>
          </CardInner>
        </Card>
        <Spacer size={16} />
        <SubCaution
          caution={
            '※売上から入金対象額へ反映されるタイミングは、お客さまが利用するクレジットカード会社や支払い時のクーポン利用有無などにより異なります。'
          }
        />
        <Spacer size={16} />
        <BlueButton
          disabled={!isAbleToTransfer}
          label={'全額入金を行う'}
          onClick={goToTransfer}
          size={'medium'}
          type={'main'}
        />
        {!isAbleToTransfer && (
          <>
            <Spacer size={16} />
            <Caution
              caution={'入金対象額が、必要な銀行振込手数料金額を下回っているため入金することができません。'}
              isBold={false}
            />
          </>
        )}
        <HorizontalLine verticalMargin={16} />
        <CardTitle title={'一部入金'} />
        <Spacer size={20} />
        <TextInput
          disabled={false}
          label={'入金金額'}
          onChange={handlePartialTransferAmount}
          required={true}
          type={'tel'}
          value={partialTransferAmount}
          size={'small'}
        />
        <Spacer size={20} />
        <BlueButton
          disabled={!isAbleToPartialTransfer}
          label={'一部入金を行う'}
          onClick={goToPartialTransfer}
          size={'medium'}
          type={'main'}
        />
      </CardInner>
    </Card>
  )
})

export default BalanceCard
